import React from 'react';

interface ZeroValueIndicatorProps {
  testName: string;
  value: number;
}

// Tests where zero is valid/expected
const ZERO_VALID_TESTS = [
  'BASOPHIL', 'EOSINOPHIL', 'RETICULOCYTE', 'RETIC',
  'IGE', 'IGG', 'IGM', 'IGA', 'ANTIBODY', 'TITER',
  'CEA', 'AFP', 'CA125', 'CA19-9', 'PSA',
  'ETHANOL', 'ALCOHOL', 'DRUG', 'COCAINE', 'MARIJUANA', 'THC',
  'HCG', 'BHCG', 'PROGESTERONE', 'ESTRADIOL'
];

// Tests where zero indicates error
const ZERO_INVALID_TESTS = [
  'HEMOGLOBIN', 'HGB', 'HEMATOCRIT', 'HCT', 'RBC', 'WBC', 'PLATELET',
  'GLUCOSE', 'SODIUM', 'POTASSIUM', 'CHLORIDE', 'CALCIUM', 'ALBUMIN',
  'MCV', 'MCH', 'MCHC', 'MPV', 'RDW', 'CREATININE'
];

export const ZeroValueIndicator: React.FC<ZeroValueIndicatorProps> = ({ testName, value }) => {
  if (value !== 0) return null;
  
  const testUpper = testName.toUpperCase();
  
  // Check if zero is valid for this test
  const isValidZero = ZERO_VALID_TESTS.some(validTest => 
    testUpper.includes(validTest) || validTest.includes(testUpper)
  );
  
  const isInvalidZero = ZERO_INVALID_TESTS.some(invalidTest => 
    testUpper.includes(invalidTest) || invalidTest.includes(testUpper)
  );
  
  if (isValidZero) {
    return (
      <div className="inline-flex items-center ml-2" title="Zero is a valid result for this test">
        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-xs text-green-600 ml-1">Valid zero</span>
      </div>
    );
  }
  
  if (isInvalidZero) {
    return (
      <div className="inline-flex items-center ml-2" title="Zero value is suspicious - please verify">
        <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-xs text-red-600 ml-1">Check value</span>
      </div>
    );
  }
  
  // Unknown test - show warning
  return (
    <div className="inline-flex items-center ml-2" title="Zero value detected - please verify">
      <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <span className="text-xs text-yellow-600 ml-1">Verify zero</span>
    </div>
  );
};